import { useEffect, useState } from "react";
import { getBrazilConfig } from "@/config/brazilExperience";

/**
 * FASE BRASIL P1 — Country detection hook with 24h localStorage cache.
 * Calls `/api/public/geoip` once per day per device.
 */
const CACHE_KEY = "vs:geo_country_v1";
const TTL_MS = 24 * 60 * 60 * 1000;

interface CacheEntry {
  country: string | null;
  ts: number;
}

function readCache(): CacheEntry | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (!parsed || typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(country: string | null) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ country, ts: Date.now() }));
  } catch {
    /* ignore */
  }
}

export interface UseGeoCountryResult {
  country: string | null;
  isBrazil: boolean;
  loading: boolean;
}

export function useGeoCountry(): UseGeoCountryResult {
  const cached = typeof window !== "undefined" ? readCache() : null;
  const [country, setCountry] = useState<string | null>(cached?.country ?? null);
  const [loading, setLoading] = useState<boolean>(!cached);

  useEffect(() => {
    if (cached) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/public/geoip", { cache: "no-store" });
        const json = (await res.json()) as { country: string | null };
        if (cancelled) return;
        const c = json?.country ?? null;
        setCountry(c);
        writeCache(c);
      } catch {
        if (!cancelled) writeCache(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cfg = typeof window !== "undefined" ? getBrazilConfig() : null;
  const forced = !!cfg?.forceBrazilMode && (cfg?.enabled ?? true);
  const enabled = cfg?.enabled ?? true;
  const isBrazil = enabled && (forced || country === "BR");
  return { country, isBrazil, loading };
}